import {
  Equipment,
  MovementInputDto,
  TreatmentPlanInputDto,
  TreatmentPlanTemplateInputDto,
} from '../backendType';
import {
  ActivityInput,
  EquipmentInput,
  TreatmentPlanInput,
} from '../../components/patient-details/treatment-plans/creation/treatmentPlanTypes';
import { ActivityDefinition, ActivityDefinitionId } from '../library';
import { getRandomItem } from '../../utils/arrayUtils';
import { isEmpty } from '../../state-manager/utils/compare';
import { addTime } from '../../utils/dateUtils';
import { parseISO } from 'date-fns';
import { normalizeOtherOptionValue } from '../../components/patient-details/treatment-plans/TreatmentPlansUtils';

// The editor adds an exercise's required devices (at their default value) only when its card is
// OPENED (FormEquipment). Saved unopened, a weighted exercise such as Bicep Curls went out with no
// Dumbbells: no load, so no weight in the patient app and no total-load goal. Fill them here, on
// save, the way the card would have: the first device of each required group, at its Default,
// unless the activity already holds a device from that group.
export function withRequiredEquipment(
  equipment: EquipmentInput[] | undefined,
  requiredEquipment: Equipment[] | undefined,
): EquipmentInput[] | undefined {
  const held = equipment ?? [];
  const seenGroups = new Set(held.map((device) => device.Group));
  const missing: EquipmentInput[] = [];
  (requiredEquipment ?? []).forEach((device) => {
    if (seenGroups.has(device.Group)) {
      return;
    }
    seenGroups.add(device.Group);
    missing.push({ ...device, IsRequired: true, Value: device.Default });
  });
  return missing.length ? [...missing, ...held] : equipment;
}

function toMovementInput(
  activity: ActivityInput,
  activitiesDefinitions: Record<ActivityDefinitionId, ActivityDefinition>,
  isTemplate: boolean,
): MovementInputDto {
  const activityDefinition = activitiesDefinitions[activity.activityDefinitionId];
  if (!activityDefinition) {
    throw new Error('missing activityDefinition for: ' + activity.activityDefinitionId);
  }
  const { settings } = activityDefinition;
  const equipment = withRequiredEquipment(
    activity.equipment,
    activityDefinition.requiredEquipment,
  );
  const movementInputDto: MovementInputDto = {
    movement_id: activityDefinition.id,
    movement_info: {},
  };
  if (settings.type) {
    if (activity.type) {
      movementInputDto.movement_info.type = activity.type;
    } else if (!isTemplate) {
      throw new Error('type in missing');
    }
  }
  if (activity.side) {
    movementInputDto.movement_info.side = activity.side;
  } else if (!isTemplate) {
    throw new Error('side in missing');
  }
  if (settings.rest) {
    if (!isEmpty(activity.rest)) {
      movementInputDto.movement_info.rest = activity.rest;
    } else if (!isTemplate) {
      throw new Error('rest in missing');
    }
  }
  if (settings.reps) {
    if (!isEmpty(activity.reps)) {
      movementInputDto.movement_info.reps = activity.reps;
    } else if (!isTemplate) {
      throw new Error('reps in missing');
    }
  }
  // Gated on the SETTING, like every sibling above — not on the value's
  // truthiness. Gating on the value meant a threshold of 0 was silently dropped
  // on save instead of being rejected: 0 is outside every exercise's allowed
  // range (the library's own minima start at 15), so it must reach the validator
  // and be flagged, not vanish. Same rule as isEmpty everywhere else: 0 is a
  // real value, only undefined/null/'' are absent.
  if (settings.minimum_to_consider_rep) {
    if (!isEmpty(activity.minimumToConsiderRep)) {
      movementInputDto.movement_info.minimumToConsiderRep = activity.minimumToConsiderRep;
    } else if (!isTemplate) {
      throw new Error('reps threshold in missing');
    }
  }
  if (!isEmpty(activity.init_angle_degrees)) {
    movementInputDto.movement_info.init_angle_degrees = activity.init_angle_degrees;
  }
  if (settings.hold) {
    if (!isEmpty(activity.hold)) {
      movementInputDto.movement_info.hold = activity.hold;
    } else if (!isTemplate) {
      throw new Error('hold in missing');
    }
  }
  // Seconds per rep. The algo reads this flat key off the prescription and paces the patient's
  // rep guide on it, overriding the exercise definition's own time_per_rep.
  if (settings.rate) {
    if (!isEmpty(activity.rate)) {
      movementInputDto.movement_info.rate = activity.rate;
    } else if (!isTemplate) {
      throw new Error('rate is missing');
    }
  }
  if (settings.sets) {
    if (!isEmpty(activity.sets)) {
      movementInputDto.movement_info.sets = activity.sets;
    } else if (!isTemplate) {
      throw new Error('sets in missing');
    }
  }
  if (settings.distance) {
    if (!isEmpty(activity.distance)) {
      movementInputDto.movement_info.distance = activity.distance;
    } else if (!isTemplate) {
      throw new Error('distance in missing');
    }
  }
  if (settings.duration) {
    if (!isEmpty(activity.duration)) {
      movementInputDto.movement_info.duration = activity.duration;
    } else if (!isTemplate) {
      throw new Error('duration in missing');
    }
  }
  if (equipment) {
    movementInputDto.movement_info.equipment = equipment.map(normalizeOtherOptionValue);
  }

  if (!isEmpty(activity.comments)) {
    movementInputDto.movement_info.comments = activity.comments;
    movementInputDto.movement_info.require_patient_acknowledgment =
      activity.require_patient_acknowledgment;
  }

  movementInputDto.movement_info.goals = Object.entries(activity.goals)
    .filter(([metric_name]) => !metric_name.startsWith('volume'))
    .map(([metric_name, value]) => {
      return { metric_name, value };
    });

  if (activityDefinition.non_algo_pem && activity.sets && activity.reps) {
    const activityIncludeBand = equipment?.find((item) => item.Name === 'Band');
    // Case-insensitive like the backend's calcVolumes: library 'Kg', custom activities 'kg'.
    const isKg = (item: { Units?: string }) => item.Units?.toLowerCase() === 'kg';
    const activityIncludeWeights = equipment?.find(isKg);
    const volumeMetrics = activityDefinition.non_algo_pem;
    const weightsMetric = volumeMetrics.find(
      (metric) => metric.metric_name == 'volume_weights',
    );
    const bandMetric = volumeMetrics.find((metric) => metric.metric_name == 'volume_band');
    const bodyMetric = volumeMetrics.find((metric) => metric.metric_name == 'volume_body');
    if (activityIncludeWeights && weightsMetric) {
      const totalWeights = equipment
        ?.filter(isKg)
        .reduce((sum, item) => sum + (Number(item.Value) || 0), 0);
      if (totalWeights) {
        movementInputDto.movement_info.goals.push({
          metric_name: weightsMetric.metric_name,
          value: totalWeights * activity.sets * activity.reps,
        });
      }
    } else if (bandMetric && activityIncludeBand) {
      movementInputDto.movement_info.goals.push({
        metric_name: bandMetric.metric_name,
        value: activity.sets * activity.reps,
      });
    } else {
      if (bodyMetric) {
        movementInputDto.movement_info.goals.push({
          metric_name: bodyMetric.metric_name,
          value: activity.sets * activity.reps,
        });
      }
    }
  }
  return movementInputDto;
}

function fullfilMissingOrganSide(
  origActivities: ActivityInput[],
  activitiesDefinitions: Record<ActivityDefinitionId, ActivityDefinition>,
) {
  const activities = [...origActivities];
  activities.forEach((activity, index) => {
    const activityDefinition = activitiesDefinitions[activity.activityDefinitionId];

    if (!activityDefinition?.settings.side) {
      activities[index] = { ...activity, side: 'NA' };
      return;
    }
    if (activities[index]?.side) {
      return;
    }
    const previousActivity = activities[index - 1];
    const nextActivity = activities[index + 1];

    if (previousActivity && !activities[index]?.side) {
      const activityDefinitionForPreviousActivity =
        activitiesDefinitions[previousActivity.activityDefinitionId];
      if (
        previousActivity.side &&
        activityDefinitionForPreviousActivity?.position === activityDefinition?.position
      ) {
        activities[index] = { ...activity, side: previousActivity.side };
      }
    }
    if (nextActivity && !activities[index]?.side) {
      const activityDefinitionForNextActivity =
        activitiesDefinitions[nextActivity.activityDefinitionId];
      if (
        nextActivity.side &&
        activityDefinitionForNextActivity?.position === activityDefinition?.position
      ) {
        activities[index] = { ...activity, side: nextActivity.side };
      }
    }
    if (previousActivity && !activities[index]?.side) {
      if (previousActivity.side) {
        activities[index] = { ...activity, side: previousActivity.side };
      }
    }
    if (nextActivity && !activities[index]?.side) {
      if (nextActivity.side) {
        activities[index] = { ...activity, side: nextActivity.side };
      }
    }
    if (!activities[index]?.side || activities[index]?.side === 'NA') {
      const sideOptions = ['Left', 'Right'];
      const side = getRandomItem(sideOptions);
      activities[index] = { ...activity, side };
    }
  });
  return activities;
}

// DEPLOY GUARD, now OFF. Kept as a switch rather than deleted: if a fleet of tablets ever turns
// out to be behind again, setting this back to false is the whole mitigation.
//
// The in-place PATCH is used ONLY while a patient is streaming (Form.tsx falls through to the
// ordinary delete-and-recreate save when no session is live), so this flag governs exactly one
// thing: what a MID-SESSION save looks like on the wire.
//
// With it false the save keeps its old shape — no slot_id, positions left exactly as the server
// assigned them — and the backend takes its legacy path: matches on position and renumbers
// nothing. That is what installed tablets require. They identify an exercise BY its position, so
// a save that renumbers makes them pair each surviving exercise with a different one's data:
// the patient silently loses an exercise mid-session, including the one they are performing.
// Removing an exercise mid-session worked before slot identity, so this is a regression to
// prevent, not merely a new capability to delay.
//
// The cost while it is false: reordering and inserting cannot be applied mid-session, which is
// exactly where they stood before. Everything else a clinician can do mid-session — swap an
// exercise, change dosage, remove one, append one — is unaffected, and so is every save outside
// a live session, which does not go through this path at all.
//
// App-side fix: patient-android #203.
export const LIVE_EDIT_SENDS_SLOT_IDENTITY = true;

// Does the plan we loaded carry slot identities?
//
// Everything about how a save is expressed hangs off this one question, so it is asked once,
// here, from the data itself rather than from a build flag. A backend that sends `slot_id`
// understands that a slot's identity is separate from its position, and the save can then say
// what it means: positions are 1..N in list order, and each entry names the slot it is.
//
// A backend that does not (one that predates slot identity, or a rollback) is handed exactly
// what it was handed before — see the legacy branch in assignPlanIndices. That matters because
// the portal and treatment-plan-handler ship as separate images: they can be skewed for minutes
// on mercury, and for longer in production if only one of them is rolled back.
//
// Delete this, and the legacy branch with it, once no deployed backend can lack slot ids.
export function planHasSlotIds(activities: ActivityInput[]): boolean {
  return activities.some((activity) => Boolean(activity.slotId));
}

// LEGACY ONLY — the spacing scheme, kept solely for a backend with no slot identity.
//
// When position and identity were the same field, a slot number could never be reused or
// renumbered, so new slots were spaced 100 apart to leave room to insert between them. That
// spacing is why a reorder was impossible: reordering meant renumbering, and renumbering meant
// re-attributing every result recorded below the exercise that moved.
export const SLOT_SPACING = 100;

// The first slot number at or after `from` that the server already knows about.
function nextExistingIndex(activities: ActivityInput[], from: number): number | undefined {
  for (let i = from + 1; i < activities.length; i += 1) {
    const candidate = activities[i]?.planIndex;
    if (typeof candidate === 'number') {
      return candidate;
    }
  }
  return undefined;
}

// An integer strictly between the two, or null when they are adjacent and none exists.
export function midpointBetween(below: number, above: number): number | null {
  const mid = Math.floor((below + above) / 2);
  return mid > below && mid < above ? mid : null;
}

// The plan_index to send for each activity, in list order.
//
// With slot identity available this is simply the POSITION — 1, 2, 3 — because that is all
// plan_index means now. It follows the clinician's list exactly, which is what makes a drag
// reorder expressible at all: the exercise keeps its slot and changes its number, instead of
// keeping its number and changing which exercise it names.
//
// Without it, the legacy branch below reproduces the old scheme byte for byte.
export function assignPlanIndices(
  activities: ActivityInput[],
  // LEGACY ONLY. The highest slot number the plan held when it was LOADED, including any the
  // clinician has since removed from the form.
  //
  // An appended row has to clear it, not merely clear the row above it. A slot being removed in
  // THIS save is still live when the server validates the body — removal is applied afterwards —
  // so numbering an append from its neighbour walks straight into it: remove the last exercise,
  // add one, and the new row is handed the number the removed one still occupies. The server
  // rejects the whole save with "slot N already exists, so it cannot be added".
  //
  // Clearing the high-water mark avoids every number the plan has ever used, and renumbers
  // nothing — which is what keeps installed tablets safe. See LIVE_EDIT_SENDS_SLOT_IDENTITY.
  reservedFloor = 0,
): number[] {
  if (planHasSlotIds(activities)) {
    return activities.map((_, i) => i + 1);
  }

  const assigned: number[] = [];

  activities.forEach((activity, i) => {
    if (typeof activity.planIndex === 'number') {
      assigned.push(activity.planIndex);
      return;
    }

    // A new row. Its number has to sit strictly between its neighbours' — that is the whole
    // contract with the server, which validates the list is strictly increasing.
    const below = assigned[i - 1];
    const above = nextExistingIndex(activities, i);

    if (below === undefined && above === undefined) {
      assigned.push(SLOT_SPACING); // a brand-new plan
    } else if (below === undefined) {
      // Prepended before everything. Halve the first slot's number rather than going negative.
      assigned.push(Math.floor((above as number) / 2));
    } else if (above === undefined) {
      // Past the end of everything the plan has ever held, not just past the row above.
      assigned.push(Math.max(below, reservedFloor) + SLOT_SPACING);
    } else {
      // Inserted between two. midpointBetween returns null when the two are adjacent and there
      // is no integer left between them — see hasRoomToInsert, which refuses that save rather
      // than renumbering to make space.
      assigned.push(midpointBetween(below, above) ?? below + 1);
    }
  });

  return assigned;
}

// Is `current` exactly what the plan editor adds on its own to a row whose stored equipment was
// empty?
//
// FormEquipment's mount effect turns a missing equipment list into [] and puts each exercise's
// required devices on it at their default value — the first device of every exclusive Group, the
// way that effect picks them. It runs as soon as a row's details render, so merely opening an
// exercise changes its equipment. On the live-session PATCH the server compares movement_info
// verbatim, reads that as a settings change, and the patient's app asks them to restart the
// exercise. Seen on mercury 2026-10-15: a pure reorder produced `equipment: null -> []` on three
// slots, and a later move produced `null -> [Chair]` on the one the patient was doing.
//
// Anything the clinician actually chose — an optional device, a different exclusive device, a
// non-default value — makes this false, so a real equipment edit is still sent.
export function isFormFilledEquipment(
  loaded: EquipmentInput[] | undefined,
  current: EquipmentInput[] | undefined,
  requiredEquipment: Equipment[] | undefined,
): boolean {
  if (loaded && loaded.length > 0) {
    return false;
  }
  const filled = current ?? [];
  const seenGroups = new Set<number>();
  const expected = (requiredEquipment ?? []).filter((device, index) => {
    if (index > 0 && seenGroups.has(device.Group)) {
      return false;
    }
    seenGroups.add(device.Group);
    return true;
  });
  if (filled.length !== expected.length) {
    return false;
  }
  return expected.every((device) =>
    filled.some(
      (item) =>
        item.Name === device.Name &&
        item.IsRequired &&
        String(item.Value ?? '') === String(device.Default ?? ''),
    ),
  );
}

export function toTreatmentPlanInputDto({
  assignorId,
  treatmentPlanForm,
  activitiesDefinitions,
  caseId,
  // PATCH only — see TreatmentPlanInputDto. `originalActivities` is the plan as
  // it was LOADED, so slot i's expected_movement_id is whatever exercise sat at
  // slot i before this edit. Both are omitted on the POST path, which leaves the
  // emitted DTO byte-identical to before.
  originalActivities,
  updateReason,
  useSlotIdentity = true,
}: {
  assignorId: string;
  treatmentPlanForm: TreatmentPlanInput;
  activitiesDefinitions: Record<ActivityDefinitionId, ActivityDefinition>;
  caseId: string;
  originalActivities?: ActivityInput[];
  updateReason?: string;
  // PATCH only. False makes this emit the pre-slot-identity body — see
  // LIVE_EDIT_SENDS_SLOT_IDENTITY, which is the only thing that passes it.
  useSlotIdentity?: boolean;
}): TreatmentPlanInputDto {
  const fixedActivities = fullfilMissingOrganSide(
    treatmentPlanForm.activities,
    activitiesDefinitions,
  );

  const startDate = addTime(treatmentPlanForm.startDate!, new Date());

  // Strip the identities ONCE, here, rather than gating each use of them. assignPlanIndices reads
  // them to decide whether to renumber positions, and the movement emitter reads them to decide
  // whether to send slot_id — and those two must never disagree. A body with renumbered positions
  // but no slot_id is the one shape the backend cannot read correctly: it matches on position,
  // against numbers that have just moved.
  const identified = useSlotIdentity
    ? fixedActivities
    : fixedActivities.map((activity) => ({ ...activity, slotId: undefined }));
  const baseline = !originalActivities
    ? originalActivities
    : useSlotIdentity
    ? originalActivities
    : originalActivities.map((activity) => ({ ...activity, slotId: undefined }));

  // Every slot number the plan held when it was loaded, removals included — see the parameter.
  const reservedFloor = (originalActivities ?? []).reduce(
    (highest, activity) => Math.max(highest, activity.planIndex ?? 0),
    0,
  );
  const planIndices = assignPlanIndices(identified, reservedFloor);

  const treatmentPlanInputDto: TreatmentPlanInputDto = {
    name: treatmentPlanForm.name!,
    assignor_user_id: assignorId,
    start_date: parseISO(startDate).toISOString(),
    description: treatmentPlanForm.description || '',
    movements: identified.map((activity, index) => {
      // plan_index is the POSITION — see assignPlanIndices. With slot ids available it simply
      // follows the clinician's list; without them it reproduces the old preserve-the-number
      // scheme, because there the number is still the identity.
      const movement = {
        // Same array, same length, mapped one-for-one just above.
        plan_index: planIndices[index]!,
        ...toMovementInput(activity, activitiesDefinitions, false),
      };
      if (!baseline) {
        return movement;
      }
      // Resolve the baseline row by the activity's OWN id, which is a client-side uuid minted
      // when the plan was loaded and preserved by every edit. Not by position — a removal or a
      // reorder makes the two lists disagree, and a positional lookup then hands the server a
      // different slot's identity and expectation, which is precisely how a good save turns
      // into a 409.
      const original = baseline.find((candidate) => candidate.id === activity.id);
      if (!original) {
        // No baseline: the clinician added this exercise. Sending neither slot_id nor
        // expected_movement_id is exactly how an addition is expressed.
        return movement;
      }
      // Prefer the PRESCRIBED movement the backend reported for this slot. The endpoint
      // validates against the raw prescription row, while the plan we loaded is RESOLVED — so
      // whenever a completed run or a continuation won the render, activityDefinitionId is the
      // exercise PERFORMED and echoing it 409s. That is precisely the mid-session case this
      // feature exists for.
      //
      // Fall back to resolving through the definitions map (a custom activity's definition id
      // is not its movement id) for a backend that predates prescribed_movement_id.
      const expected =
        original.prescribedMovementId ??
        activitiesDefinitions[original.activityDefinitionId]?.id;
      // Equipment the EDITOR filled in, not the clinician, must go back exactly as it was
      // stored. Otherwise the server reads it as a settings change and asks a patient who is
      // mid-session to restart an exercise nobody touched. See isFormFilledEquipment.
      if (
        original.activityDefinitionId === activity.activityDefinitionId &&
        isFormFilledEquipment(
          original.equipment,
          // Compare what is actually sent: an unopened card's required devices were filled in
          // by withRequiredEquipment, which is the editor's default too, not a clinician edit.
          withRequiredEquipment(
            activity.equipment,
            activitiesDefinitions[activity.activityDefinitionId]?.requiredEquipment,
          ),
          activitiesDefinitions[activity.activityDefinitionId]?.requiredEquipment,
        )
      ) {
        if (original.equipment === undefined) {
          delete movement.movement_info.equipment;
        } else {
          movement.movement_info.equipment = original.equipment;
        }
      }
      return {
        ...movement,
        // The slot this entry IS. Present only when the backend gave us one, which is what
        // tells it the body can be read as positions-plus-identities rather than as the old
        // position-is-identity shape.
        ...(original.slotId ? { slot_id: original.slotId } : {}),
        ...(expected ? { expected_movement_id: expected } : {}),
      };
    }),
    comments: [],
    state: '',
    case_id: caseId,
  };
  if (updateReason) {
    treatmentPlanInputDto.update_reason = updateReason;
  }
  return treatmentPlanInputDto;
}

export function toTreatmentPlanTemplateInputDto(
  treatmentPlanForm: TreatmentPlanInput,
  activitiesDefinitions: Record<ActivityDefinitionId, ActivityDefinition>,
): TreatmentPlanTemplateInputDto {
  const treatmentPlanTemplateInputDto: TreatmentPlanTemplateInputDto = {
    name: treatmentPlanForm.name!,
    description: treatmentPlanForm.description || '',
    movements: treatmentPlanForm.activities.map((activity, index) => {
      return {
        plan_index: index + 1,
        ...toMovementInput(activity, activitiesDefinitions, true),
      };
    }),
    state: '',
  };
  return treatmentPlanTemplateInputDto;
}
